import {
  FREE_GAMES_PER_WINDOW,
  getFreeGameQuotaWindowStart,
  getNextFreeGameQuotaReset,
  normalizeAccountTimeZone,
} from '@repo/sdk'

export const ANONYMOUS_FREE_GAME_USAGE_STORAGE_KEY
  = 'mentiss_anonymous_free_game_usage_v3'

const PREVIOUS_ANONYMOUS_FREE_GAME_USAGE_STORAGE_KEY
  = 'mentiss_anonymous_free_game_usage_v2'

const LEGACY_ANONYMOUS_FREE_GAME_USAGE_STORAGE_KEY
  = 'mentiss_anonymous_free_game_usage_v1'

export interface AnonymousFreeGameUsageStorage {
  getItem: (key: string) => string | null
  removeItem: (key: string) => void
  setItem: (key: string, value: string) => void
}

interface StoredAnonymousFreeGameUsage {
  gameIds?: string[]
  used: number
  windowStartMs: number
  timeZone?: string
}

interface CurrentAnonymousFreeGameUsage {
  gameIds: string[]
  used: number
}

const UNAVAILABLE_STORAGE: AnonymousFreeGameUsageStorage = {
  getItem: () => null,
  removeItem: () => {},
  setItem: () => {},
}

function defaultStorage(): AnonymousFreeGameUsageStorage {
  try {
    return typeof window === 'undefined'
      ? UNAVAILABLE_STORAGE
      : window.localStorage
  }
  catch {
    return UNAVAILABLE_STORAGE
  }
}

function selectedTimeZone(
  storage: AnonymousFreeGameUsageStorage,
  timeZone?: string,
): string {
  if (timeZone)
    return normalizeAccountTimeZone(timeZone)
  try {
    const raw = storage.getItem(ANONYMOUS_FREE_GAME_USAGE_STORAGE_KEY)
    const saved = raw ? JSON.parse(raw) as Partial<StoredAnonymousFreeGameUsage> : null
    if (typeof saved?.timeZone === 'string')
      return normalizeAccountTimeZone(saved.timeZone)
    return normalizeAccountTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone)
  }
  catch {
    return 'UTC'
  }
}

function fixedWindowStartMs(nowMs: number, timeZone: string): number {
  const requestedDate = new Date(nowMs)
  const now = Number.isFinite(nowMs) && !Number.isNaN(requestedDate.getTime())
    ? requestedDate
    : new Date()
  return getFreeGameQuotaWindowStart(now, timeZone).getTime()
}

function clampUsage(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value))
    return 0
  return Math.min(
    FREE_GAMES_PER_WINDOW,
    Math.max(0, Math.floor(value)),
  )
}

function readCurrentWindowUsage(
  storage: AnonymousFreeGameUsageStorage,
  nowMs: number,
  timeZone: string,
): CurrentAnonymousFreeGameUsage {
  try {
    storage.removeItem(LEGACY_ANONYMOUS_FREE_GAME_USAGE_STORAGE_KEY)
  }
  catch {}
  try {
    const raw = storage.getItem(ANONYMOUS_FREE_GAME_USAGE_STORAGE_KEY)
      ?? storage.getItem(PREVIOUS_ANONYMOUS_FREE_GAME_USAGE_STORAGE_KEY)
    if (!raw)
      return { gameIds: [], used: 0 }

    const stored = JSON.parse(raw) as Partial<StoredAnonymousFreeGameUsage>
    if (
      !stored
      || typeof stored !== 'object'
      || Array.isArray(stored)
      || typeof stored.windowStartMs !== 'number'
      || !Number.isFinite(stored.windowStartMs)
    ) {
      return { gameIds: [], used: 0 }
    }

    const currentStart = fixedWindowStartMs(nowMs, timeZone)
    const currentEnd = getNextFreeGameQuotaReset(new Date(nowMs), timeZone).getTime()
    const storedZone = normalizeAccountTimeZone(stored.timeZone)
    const storedEnd = getNextFreeGameQuotaReset(new Date(stored.windowStartMs), storedZone).getTime()
    // Version 2 retained only a UTC day, not each start instant. Carry usage
    // across an overlapping account day conservatively during this one-time move.
    if (stored.windowStartMs >= currentEnd || storedEnd <= currentStart)
      return { gameIds: [], used: 0 }

    const used = clampUsage(stored.used)
    const gameIds = Array.isArray(stored.gameIds)
      ? [...new Set(
          stored.gameIds
            .filter(gameId => typeof gameId === 'string')
            .map(gameId => gameId.trim())
            .filter(Boolean),
        )].slice(0, used)
      : []
    const usage = { gameIds, used }
    if ((stored.timeZone !== timeZone || stored.windowStartMs !== currentStart)
      && writeCurrentWindowUsage(storage, nowMs, usage, timeZone)) {
      storage.removeItem(PREVIOUS_ANONYMOUS_FREE_GAME_USAGE_STORAGE_KEY)
    }
    return usage
  }
  catch {
    return { gameIds: [], used: 0 }
  }
}

function writeCurrentWindowUsage(
  storage: AnonymousFreeGameUsageStorage,
  nowMs: number,
  usage: CurrentAnonymousFreeGameUsage,
  timeZone: string,
): boolean {
  try {
    const next: StoredAnonymousFreeGameUsage = {
      gameIds: usage.gameIds.slice(0, FREE_GAMES_PER_WINDOW),
      used: clampUsage(usage.used),
      windowStartMs: fixedWindowStartMs(nowMs, timeZone),
      timeZone,
    }
    storage.setItem(
      ANONYMOUS_FREE_GAME_USAGE_STORAGE_KEY,
      JSON.stringify(next),
    )
    return true
  }
  catch {
    return false
  }
}

export function readAnonymousFreeGamesUsed(
  storage: AnonymousFreeGameUsageStorage = defaultStorage(),
  nowMs = Date.now(),
  timeZone?: string,
): number {
  return readCurrentWindowUsage(storage, nowMs, selectedTimeZone(storage, timeZone)).used
}

export function incrementAnonymousFreeGamesUsed(
  storage: AnonymousFreeGameUsageStorage = defaultStorage(),
  nowMs = Date.now(),
  timeZone?: string,
): number {
  const current = readCurrentWindowUsage(storage, nowMs, selectedTimeZone(storage, timeZone))
  const next = {
    gameIds: current.gameIds,
    used: Math.min(FREE_GAMES_PER_WINDOW, current.used + 1),
  }
  writeCurrentWindowUsage(storage, nowMs, next, selectedTimeZone(storage, timeZone))
  return next.used
}

export function recordAnonymousFreeGameStarted(
  gameId: string,
  storage: AnonymousFreeGameUsageStorage = defaultStorage(),
  nowMs = Date.now(),
  timeZone?: string,
): number {
  const normalizedGameId = typeof gameId === 'string' ? gameId.trim() : ''
  const current = readCurrentWindowUsage(storage, nowMs, selectedTimeZone(storage, timeZone))
  if (
    !normalizedGameId
    || current.used >= FREE_GAMES_PER_WINDOW
    || current.gameIds.includes(normalizedGameId)
  ) {
    return current.used
  }

  const next = {
    gameIds: [...current.gameIds, normalizedGameId],
    used: current.used + 1,
  }
  writeCurrentWindowUsage(storage, nowMs, next, selectedTimeZone(storage, timeZone))
  return next.used
}

export function recordAnonymousFreeGameStartedInWindow(
  gameId: string,
  requestWindowStartMs: number,
  storage: AnonymousFreeGameUsageStorage = defaultStorage(),
  nowMs = Date.now(),
  timeZone?: string,
): number {
  if (fixedWindowStartMs(nowMs, selectedTimeZone(storage, timeZone)) !== requestWindowStartMs)
    return readAnonymousFreeGamesUsed(storage, nowMs, timeZone)
  return recordAnonymousFreeGameStarted(gameId, storage, nowMs, timeZone)
}

export function releaseAnonymousFreeGame(
  gameId: string,
  storage: AnonymousFreeGameUsageStorage = defaultStorage(),
  nowMs = Date.now(),
  timeZone?: string,
): number {
  const normalizedGameId = typeof gameId === 'string' ? gameId.trim() : ''
  const current = readCurrentWindowUsage(storage, nowMs, selectedTimeZone(storage, timeZone))
  if (!normalizedGameId || !current.gameIds.includes(normalizedGameId))
    return current.used

  const next = {
    gameIds: current.gameIds.filter(id => id !== normalizedGameId),
    used: Math.max(0, current.used - 1),
  }
  writeCurrentWindowUsage(storage, nowMs, next, selectedTimeZone(storage, timeZone))
  return next.used
}

export function resetAnonymousFreeGamesUsed(
  storage: AnonymousFreeGameUsageStorage = defaultStorage(),
): void {
  try {
    storage.removeItem(ANONYMOUS_FREE_GAME_USAGE_STORAGE_KEY)
    storage.removeItem(PREVIOUS_ANONYMOUS_FREE_GAME_USAGE_STORAGE_KEY)
    storage.removeItem(LEGACY_ANONYMOUS_FREE_GAME_USAGE_STORAGE_KEY)
  }
  catch {}
}
